import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Pencil } from "lucide-react";
import {
  useUpdateContract,
  getGetContractQueryKey,
  getListContractsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";

interface CurrentTerms {
  annualSalary?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  buyoutClause?: number | null;
}

interface ReviseContractTermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: number;
  current: CurrentTerms;
  onSaved?: () => void;
}

/** Admin path to update salary / dates / buyout after create, including during negotiation. */
export function ReviseContractTermsDialog({
  open,
  onOpenChange,
  contractId,
  current,
  onSaved,
}: ReviseContractTermsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateContract = useUpdateContract();

  const [annualSalary, setAnnualSalary] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [buyoutClause, setBuyoutClause] = useState("");

  useEffect(() => {
    if (!open) return;
    setAnnualSalary(current.annualSalary != null ? String(current.annualSalary) : "");
    setStartDate(current.startDate ? String(current.startDate).slice(0, 10) : "");
    setEndDate(current.endDate ? String(current.endDate).slice(0, 10) : "");
    setBuyoutClause(current.buyoutClause != null ? String(current.buyoutClause) : "");
    // Seed only on open so parent re-renders do not wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open]);

  function parseNum(s: string): number | null {
    if (!s.trim()) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  const handleSave = () => {
    const salary = parseNum(annualSalary);
    const buyout = parseNum(buyoutClause);
    if (!startDate.trim() || !endDate.trim()) {
      toast({
        title: "Dates required",
        description: "Start and end dates are required.",
        variant: "destructive",
      });
      return;
    }
    if (salary == null || salary < 0) {
      toast({
        title: "Salary required",
        description: "Enter a valid annual salary.",
        variant: "destructive",
      });
      return;
    }

    updateContract.mutate(
      {
        id: contractId,
        data: {
          annualSalary: salary,
          startDate: startDate.trim(),
          endDate: endDate.trim(),
          buyoutClause: buyout,
        } as Parameters<typeof updateContract.mutate>[0]["data"],
      },
      {
        onSuccess: () => {
          toast({
            title: "Terms updated",
            description: "Compensation and dates saved. Send a revision when ready.",
          });
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          onOpenChange(false);
          onSaved?.();
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not save terms" }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="revise-contract-terms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Revise terms
          </DialogTitle>
          <DialogDescription>
            Update salary, term dates, or buyout to reflect negotiation. Bonuses and allowances
            still edit from the Details tab. After saving, send another revision from Negotiation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="revise-annual-salary">Annual salary</Label>
            <Input
              id="revise-annual-salary"
              type="number"
              min={0}
              value={annualSalary}
              onChange={(e) => setAnnualSalary(e.target.value)}
              data-testid="input-revise-annual-salary"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="revise-start-date">Start date</Label>
              <Input
                id="revise-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="input-revise-start-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="revise-end-date">End date</Label>
              <Input
                id="revise-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="input-revise-end-date"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="revise-buyout">Buyout clause</Label>
            <Input
              id="revise-buyout"
              type="number"
              min={0}
              value={buyoutClause}
              onChange={(e) => setBuyoutClause(e.target.value)}
              data-testid="input-revise-buyout"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={updateContract.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateContract.isPending}
            data-testid="button-save-revised-contract-terms"
          >
            {updateContract.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              "Save revised terms"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
